import React from 'react';
import { Bar, Line, Pie, Doughnut, Radar } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
ChartJS.register(...registerables);

type ChartType = 'bar' | 'line' | 'pie' | 'doughnut' | 'area' | 'radar';

interface ChartRendererProps {
  type: ChartType;
  data: any[];
  labelsKey: string;
  valueKeys: string[];
}

const ChartRenderer: React.FC<ChartRendererProps> = ({ type, data, labelsKey, valueKeys }) => {
  const labels = data.map((row) => row[labelsKey]);

  const datasetColors = [
    '#3b82f6', '#f97316', '#10b981', '#6366f1', '#f59e0b', '#ec4899', '#14b8a6', '#8b5cf6'
  ];

  const datasets = valueKeys.map((key, i) => ({
    label: key,
    data: data.map((row) => Number(row[key]) || 0),
    backgroundColor: datasetColors[i % datasetColors.length],
    borderColor: datasetColors[i % datasetColors.length],
    fill: type === 'area',
  }));

  const chartData = { labels, datasets };

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: '#374151',
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#4b5563',
        },
        grid: {
          color: '#e5e7eb',
        },
      },
      y: {
        ticks: {
          color: '#4b5563',
        },
        grid: {
          color: '#e5e7eb',
        },
      },
    },
  };

  const chartProps = {
    data: chartData,
    options: baseOptions,
  };

  if (!labelsKey || valueKeys.length === 0) {
    return <div className="text-gray-500 text-sm italic">No data available for chart. Please select numeric columns.</div>;
  }

  return (
    <div className="w-full h-[400px]">
      {type === 'bar' && <Bar {...chartProps} />}
      {type === 'line' && <Line {...chartProps} />}
      {type === 'area' && (
        <Line
          data={chartData}
          options={{
            ...baseOptions,
            elements: { line: { tension: 0.4 }, point: { radius: 0 } },
          }}
        />
      )}
      {type === 'pie' && <Pie {...chartProps} />}
      {type === 'doughnut' && <Doughnut {...chartProps} />}
      {type === 'radar' && <Radar {...chartProps} />}
    </div>
  );
};

export default ChartRenderer;
